import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  fetchFilteredCarbonAssets,
  formatNumber,
  formatDate,
  truncateText,
} from "../../config/wilder_requests";
import AssetDetailsModal from "../landing/AssetDetailsModal";
import PurchaseRequestModal from "../landing/PurchaseRequestModal";
import { b4PublicImage } from "./b4Media";
import {
  SectionInner,
  SectionMuted,
  Eyebrow,
  H2,
  FeaturedShowcaseStack,
  FeaturedShowcaseCard,
  FeaturedShowcaseCopyCol,
  FeaturedShowcaseHeroCol,
  FeaturedShowcaseHeroFrame,
  FeaturedShowcaseHeroImg,
  FeaturedShowcaseProtocolBand,
  FeaturedShowcaseProtocolImg,
  FeaturedImageLightboxBackdrop,
  FeaturedImageLightboxPanel,
  FeaturedImageLightboxImg,
  FeaturedImageLightboxClose,
  ProjectGrid,
  ProjectCard,
  ProjectCardActions,
  ProjectCardActionGhost,
  ProjectCardActionPrimary,
  Tag,
  CardTitle,
  CardText,
  BtnPrimary,
  BtnGhost,
} from "./styles";
import { useLanguage } from "../../contexts/LanguageContext";
import { getTranslation } from "../../locales";
import { buildSocialCarbonIssuanceUrl } from "../../utils/socialCarbonRegistry";

const Centered = ({ children }) => (
  <div
    style={{
      textAlign: "center",
      padding: "2.5rem 1rem",
      color: "#a9c0cc",
      fontSize: "0.9375rem",
    }}
  >
    {children}
  </div>
);

const Spinner = () => (
  <div
    style={{
      width: 40,
      height: 40,
      margin: "0 auto",
      border: "3px solid rgba(37, 208, 127, 0.25)",
      borderTopColor: "#25d07f",
      borderRadius: "50%",
      animation: "b4spin 0.9s linear infinite",
    }}
  />
);

export default function B4CarbonAssetsSection() {
  const { language } = useLanguage();
  const t = getTranslation(language).b4CarbonSection;
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedName, setSelectedName] = useState("");
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [purchasePackageName, setPurchasePackageName] = useState("");
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async () => {
    const tr = getTranslation(language).b4CarbonSection;
    try {
      setLoading(true);
      setError(null);
      const list = await fetchFilteredCarbonAssets();
      setAssets(list);
    } catch (err) {
      console.error("Erro ao buscar pacotes de crédito:", err);
      setError(err.message || tr.loadErrorFallback);
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [lightbox]);

  const openModal = (name) => {
    setSelectedName(name);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedName("");
  };

  const openPurchaseModal = (name) => {
    setPurchasePackageName(name || "");
    setPurchaseModalOpen(true);
  };

  const closePurchaseModal = () => {
    setPurchaseModalOpen(false);
    setPurchasePackageName("");
  };

  return (
    <>
      <style>
        {`@keyframes b4spin { to { transform: rotate(360deg); } }`}
      </style>
      <SectionMuted id="projetos">
        <SectionInner>
          <Eyebrow>{t.showcaseEyebrow}</Eyebrow>
          <H2>{t.showcaseTitle}</H2>

          <FeaturedShowcaseStack>
            {t.featuredProjects.map((fp) => (
              <FeaturedShowcaseCard key={fp.href}>
                <FeaturedShowcaseCopyCol>
                  <FeaturedShowcaseProtocolBand>
                    <FeaturedShowcaseProtocolImg
                      src={b4PublicImage(fp.protocolLogo)}
                      alt={fp.protocolAlt}
                    />
                  </FeaturedShowcaseProtocolBand>
                  <CardText
                    style={{
                      color: "#e8f4fc",
                      fontSize: "clamp(0.98rem, 1.35vw, 1.06rem)",
                      lineHeight: 1.78,
                      margin: 0,
                      maxWidth: "32rem",
                      textAlign: "center",
                    }}
                  >
                    {fp.body}
                  </CardText>
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      gap: "0.75rem",
                    }}
                  >
                    <BtnPrimary
                      href={fp.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: "1.05rem 2rem",
                        fontSize: "1.06rem",
                        fontWeight: 700,
                        letterSpacing: "0.02em",
                        boxShadow: "0 10px 32px rgba(0, 176, 86, 0.5)",
                        textAlign: "center",
                      }}
                    >
                      {t.ctaLearnProject}
                    </BtnPrimary>
                    {fp.blockchainRegistryHref ? (
                      <BtnGhost
                        href={fp.blockchainRegistryHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          padding: "1.05rem 1.65rem",
                          fontSize: "0.98rem",
                          fontWeight: 600,
                        }}
                      >
                        {t.ctaBlockchainRegistry}
                      </BtnGhost>
                    ) : null}
                  </div>
                </FeaturedShowcaseCopyCol>

                <FeaturedShowcaseHeroCol>
                  <FeaturedShowcaseHeroFrame
                    type="button"
                    aria-label={`${t.expandImageAria} — ${fp.certificateAlt}`}
                    onClick={() =>
                      setLightbox({
                        src: b4PublicImage(fp.certificateImage),
                        alt: fp.certificateAlt,
                      })
                    }
                  >
                    <FeaturedShowcaseHeroImg
                      src={b4PublicImage(fp.certificateImage)}
                      alt={fp.certificateAlt}
                    />
                  </FeaturedShowcaseHeroFrame>
                </FeaturedShowcaseHeroCol>
              </FeaturedShowcaseCard>
            ))}
          </FeaturedShowcaseStack>

          {lightbox
            ? createPortal(
                <FeaturedImageLightboxBackdrop
                  role="dialog"
                  aria-modal="true"
                  aria-label={lightbox.alt}
                  onClick={() => setLightbox(null)}
                >
                  <FeaturedImageLightboxClose
                    type="button"
                    aria-label={t.closeLightbox}
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightbox(null);
                    }}
                  >
                    ×
                  </FeaturedImageLightboxClose>
                  <FeaturedImageLightboxPanel
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FeaturedImageLightboxImg
                      src={lightbox.src}
                      alt={lightbox.alt}
                    />
                  </FeaturedImageLightboxPanel>
                </FeaturedImageLightboxBackdrop>,
                document.body
              )
            : null}

          {loading && (
            <Centered>
              <Spinner />
              <p style={{ marginTop: "1rem" }}>{t.loading}</p>
            </Centered>
          )}

          {!loading && error && (
            <Centered style={{ color: "#ff8a8a" }}>
              {error}
              <div style={{ marginTop: "1rem" }}>
                <BtnGhost
                  type="button"
                  onClick={() => load()}
                  style={{ padding: "0.65rem 1.25rem", fontSize: "0.8125rem" }}
                >
                  {t.tryAgain}
                </BtnGhost>
              </div>
            </Centered>
          )}

          {!loading && !error && (
            <ProjectGrid>
              {assets.map((asset, index) => {
                const name =
                  asset["Asset Name"] ||
                  `${t.packageFallbackPrefix} ${index + 1}`;
                const location = asset["Project type"] || asset["Asset type"] || "—";
                const typeLabel =
                  asset["Asset type"] || asset.Standard || t.defaultAssetType;
                const vintage =
                  asset.Vintage != null && String(asset.Vintage).trim() !== ""
                    ? String(asset.Vintage).trim()
                    : null;
                const originationTag = vintage
                  ? `${t.originationPeriodLabel} ${vintage}`
                  : t.originationPeriodLabel;
                const qty = asset["Quantity issued"] ?? asset.Quantity;
                const impact = [
                  asset.Standard && `${t.impactStandard}: ${asset.Standard}`,
                  vintage && `${t.impactVintage}: ${vintage}`,
                  qty != null &&
                    `${formatNumber(qty)} ${t.impactCreditsIssued}`,
                  asset["Created Date"] &&
                    `${t.impactRegistry}: ${formatDate(asset["Created Date"])}`,
                ]
                  .filter(Boolean)
                  .join(" · ");

                const socialCarbonUrl = buildSocialCarbonIssuanceUrl(asset._id);

                return (
                  <ProjectCard key={asset._id || `${name}-${index}`}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.9rem" }}>
                      <Tag>{originationTag}</Tag>
                      <CardTitle style={{ marginBottom: 0 }}>
                        <button
                          type="button"
                          onClick={() => openModal(name)}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            margin: 0,
                            color: "inherit",
                            font: "inherit",
                            fontWeight: 700,
                            textAlign: "left",
                            cursor: "pointer",
                            textDecoration: "underline",
                            textDecorationColor: "rgba(78, 216, 255, 0.35)",
                          }}
                          title={t.viewPackageDetailsTitle}
                        >
                          {truncateText(name, 72)}
                        </button>
                      </CardTitle>
                      <CardText>
                        <strong style={{ color: "#f5fbff" }}>
                          {truncateText(String(location), 40)}
                        </strong>
                        <br />
                        {typeLabel}
                      </CardText>
                    </div>
                    <CardText style={{ marginTop: "0.15rem" }}>
                      {impact || t.detailsOnRegistry}
                    </CardText>
                    <ProjectCardActions>
                      {socialCarbonUrl ? (
                        <ProjectCardActionGhost
                          href={socialCarbonUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t.wilderSocialCarbonDetails}
                        </ProjectCardActionGhost>
                      ) : null}
                      <ProjectCardActionGhost
                        as="button"
                        type="button"
                        onClick={() => openModal(name)}
                      >
                        {t.packageDetails}
                      </ProjectCardActionGhost>
                      <ProjectCardActionPrimary
                        type="button"
                        onClick={() => openPurchaseModal(name)}
                      >
                        {t.purchaseRequest}
                      </ProjectCardActionPrimary>
                    </ProjectCardActions>
                  </ProjectCard>
                );
              })}
            </ProjectGrid>
          )}

          {!loading && !error && assets.length === 0 && (
            <Centered>
              {t.noPackages}
            </Centered>
          )}
        </SectionInner>
      </SectionMuted>

      <AssetDetailsModal
        isOpen={modalOpen}
        onClose={closeModal}
        assetName={selectedName}
      />

      <PurchaseRequestModal
        isOpen={purchaseModalOpen}
        onClose={closePurchaseModal}
        packageName={purchasePackageName}
      />
    </>
  );
}
